import React, { useEffect, useMemo, useState } from 'react';
import { asDate, browserTz, fmtTime, schedulingAPI } from '../../lib/schedulingApi';

const HOUR = 3600000;
const PROVIDER = { microsoft: 'Outlook', google: 'Google Calendar' };
const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const clip = (items, a, b) => (items || [])
  .map((x) => ({ ...x, s: Math.max(asDate(x.start).getTime(), a), e: Math.min(asDate(x.end).getTime(), b) }))
  .filter((x) => x.e > x.s);
const merge = (items) => [...items].sort((x, y) => x.s - y.s).reduce((acc, x) => {
  const last = acc[acc.length - 1];
  if (last && x.s <= last.e) last.e = Math.max(last.e, x.e);
  else acc.push({ ...x });
  return acc;
}, []);

const calLabel = (m) => {
  const c = m.calendars[0];
  if (!c) return 'No calendar connected';
  if (c.status !== 'connected') return `${PROVIDER[c.provider]} needs reconnecting`;
  return `${PROVIDER[c.provider]} connected`;
};

const Block = ({ x, kind, pct, label, testId }) => (
  <i className={`sc-bp-blk is-${kind}`} style={{ left: `${pct(x.s)}%`, width: `${pct(x.e) - pct(x.s)}%` }} title={label} data-testid={testId}>
    {kind !== 'hours' && kind !== 'open' ? label : null}
  </i>
);

const Lane = ({ m, a, b, pct }) => (
  <div className="sc-bp-lane" data-testid={`busy-preview-lane-${m.id}`}>
    <div className="sc-bp-name">
      <b><span>{m.name}</span>{m.is_me && <em data-testid="busy-preview-you">you</em>}</b>
      <span>{m.hours_set ? calLabel(m) : 'Hours not set · default 9–5 UTC'}</span>
    </div>
    <div className="sc-bp-track">
      {clip(m.hours, a, b).map((x, i) => <Block key={`h${i}`} x={x} kind="hours" pct={pct} label="Working hours" />)}
      {clip(m.interviews, a, b).map((x, i) => <Block key={`i${i}`} x={x} kind="iv" pct={pct} label={x.title} testId="busy-preview-interview-block" />)}
      {clip(m.busy, a, b).map((x, i) => <Block key={`b${i}`} x={x} kind="busy" pct={pct} label={x.title || 'Busy'} testId="busy-preview-busy-block" />)}
    </div>
  </div>
);

const Legend = () => (
  <div className="sc-bp-legend">
    <span><i className="is-hours" />Working hours</span>
    <span><i className="is-iv" />CHIRE interview</span>
    <span><i className="is-busy" />Calendar busy</span>
    <span><i className="is-open" />Open for candidates</span>
  </div>
);

function dayWindow(members, start) {
  const end = start + 24 * HOUR;
  const hrs = members.flatMap((m) => clip(m.hours, start, end));
  let lo = hrs.length ? Math.floor((Math.min(...hrs.map((x) => x.s)) - start) / HOUR) : 8;
  let hi = hrs.length ? Math.ceil((Math.max(...hrs.map((x) => x.e)) - start) / HOUR) : 18;
  if (hi - lo < 4) { lo = Math.max(0, lo - 2); hi = Math.min(24, hi + 2); }
  return [lo, hi];
}

// Shows, before a self-schedule link goes out, which hours / interviews / calendar events block each
// pool member and the slots candidates will actually be offered. Others' events are plain "Busy".
export default function BusyPreview({ poolId, duration, panel, notice }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [day, setDay] = useState(0);
  const tz = browserTz();

  useEffect(() => {
    if (!poolId) return undefined;
    let live = true;
    setData(null);
    setErr(false);
    const t = setTimeout(() => {
      schedulingAPI.poolPreview(poolId, { duration, panel, notice })
        .then(({ data: d }) => { if (live) setData(d); })
        .catch(() => { if (live) setErr(true); });
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [poolId, duration, panel, notice]);

  const days = useMemo(() => {
    const today = new Date();
    return Array.from({ length: 7 }, (_, i) => midnight(new Date(today.getFullYear(), today.getMonth(), today.getDate() + i)));
  }, []);
  const perDay = useMemo(() => days.map((d) => (data?.slots || []).filter((s) => midnight(asDate(s.start)) === d)), [data, days]);
  useEffect(() => {
    if (!data) return;
    const first = perDay.findIndex((x) => x.length);
    setDay(first < 0 ? 0 : first);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!poolId) return null;
  if (err) return <div className="iv-note" data-testid="busy-preview-error">Couldn't load the availability preview. You can still send the link.</div>;
  if (!data) return <div className="sc-bp iv-small" data-testid="busy-preview-loading">Checking the pool's calendars…</div>;

  const start = days[day];
  const [lo, hi] = dayWindow(data.members, start);
  const a = start + lo * HOUR;
  const b = start + hi * HOUR;
  const pct = (t) => ((t - a) / (b - a)) * 100;
  const open = merge(clip(perDay[day], a, b));
  const total = perDay.reduce((n, x) => n + x.length, 0);
  const grid = { backgroundImage: 'linear-gradient(90deg, #E2E8F0 1px, transparent 1px)', backgroundSize: `${100 / (hi - lo)}% 100%` };

  return (
    <div className="sc-bp" data-testid="busy-preview">
      <div className="sc-bp-head">
        <b>What candidates will see</b>
        <span className="iv-small" data-testid="busy-preview-total">Next 7 days · your timezone ({tz}) · {total} open slot{total === 1 ? '' : 's'}</span>
      </div>
      <div className="sc-days">
        {days.map((d, i) => {
          const dt = new Date(d);
          return (
            <button type="button" key={d} className={`sc-day ${i === day ? 'is-on' : ''}`} onClick={() => setDay(i)} data-testid={`busy-preview-day-${i}`}>
              <span>{dt.toLocaleDateString(undefined, { weekday: 'short' })}</span>
              <b>{dt.getDate()}</b>
              <small>{perDay[i].length ? `${perDay[i].length} open` : 'none'}</small>
            </button>
          );
        })}
      </div>
      <div className="sc-bp-scroll">
        <div className="sc-bp-grid">
          <div className="sc-bp-axis">
            <span />
            <div className="sc-bp-ticks">
              {Array.from({ length: hi - lo + 1 }, (_, i) => i).filter((i) => i % (hi - lo > 8 ? 2 : 1) === 0).map((i) => (
                <span key={i} style={{ left: `${(i / (hi - lo)) * 100}%`, ...(i === hi - lo ? { transform: 'translateX(-100%)' } : {}) }}>{new Date(a + i * HOUR).toLocaleTimeString(undefined, { hour: 'numeric' })}</span>
              ))}
            </div>
          </div>
          {data.members.map((m) => <Lane key={m.id} m={m} a={a} b={b} pct={pct} />)}
          <div className="sc-bp-lane is-open-lane" data-testid="busy-preview-open-lane">
            <div className="sc-bp-name"><b>Candidates see</b><span>{perDay[day].length} slot{perDay[day].length === 1 ? '' : 's'} · {duration} min</span></div>
            <div className="sc-bp-track" style={grid}>
              {open.map((x) => <Block key={x.s} x={x} kind="open" pct={pct} label="Open for candidates" testId="busy-preview-open-block" />)}
            </div>
          </div>
        </div>
      </div>
      <div className="sc-bp-times" data-testid="busy-preview-times">
        {perDay[day].length ? perDay[day].slice(0, 24).map((s) => <span key={s.start}>{fmtTime(s.start, tz)}</span>)
          : <em className="iv-small">No open slots this day{notice ? ` (minimum notice ${notice}h)` : ''}.</em>}
        {perDay[day].length > 24 && <em className="iv-small">+{perDay[day].length - 24} more</em>}
      </div>
      <Legend />
      {data.members.some((m) => !m.calendars.some((c) => c.status === 'connected')) && (
        <div className="iv-small" style={{ marginTop: '0.4rem' }} data-testid="busy-preview-no-calendar-note">Members without a connected calendar are only blocked by their hours and CHIRE interviews.</div>
      )}
    </div>
  );
}
